import { useCallback, useMemo } from 'react';

import {
  type CalendarHolidayInfo,
  type HabitSuggestion,
  type HolidaySuggestion,
} from '@/store/proactive/proactive.mappers';
import {
  dayKey,
  useHabitSuggestionsByDay,
  useHolidaysByYears,
  useHolidaySuggestionsByDay,
} from '@/store/proactive/proactive.store';
import { getUsHoliday, type UsHoliday } from '@/utils/usHolidays';

export type CalendarHoliday = {
  holiday: UsHoliday;
  /** The holiday as core-ai knows it: its key and whether the agent can write an idea for it. */
  info?: CalendarHolidayInfo;
  /** An open or snoozed AI idea for this holiday — the day is highlighted and clickable. */
  suggestion?: HolidaySuggestion;
};

const THIS_YEAR = new Date().getFullYear();
// The calendar can be paged a year either way; holidays are static per year.
const YEARS = [THIS_YEAR - 1, THIS_YEAR, THIS_YEAR + 1];

/**
 * What the calendar shows on a day: the US holiday (federal from `usHolidays`, plus
 * the celebration ones core-ai knows — Halloween, New Year's Eve, Mother's Day…),
 * and the AI idea for it when there is one. Every holiday is keyed by the day its
 * badge sits on (the observed day for federal holidays). Also the day a habit
 * suggestion is about (`habitOn`), which gets its own badge.
 */
export const useCalendarHolidays = () => {
  const suggestionsByDay = useHolidaySuggestionsByDay();
  const habitsByDay = useHabitSuggestionsByDay();
  const holidays = useHolidaysByYears(YEARS);

  const infoByDay = useMemo(() => {
    const map = new Map<string, CalendarHolidayInfo>();
    for (const info of holidays) map.set(dayKey(info.observedDate), info);
    return map;
  }, [holidays]);

  const holidayOn = useCallback(
    (date: Date): CalendarHoliday | undefined => {
      const key = dayKey(date);
      const suggestion = suggestionsByDay.get(key);
      const info = infoByDay.get(key);
      const federal = getUsHoliday(date);
      if (federal) return { holiday: federal, info, suggestion };
      const source = info ?? suggestion?.holiday;
      if (!source) return undefined;
      return {
        holiday: { date: source.observedDate, name: source.name, observed: false },
        info,
        suggestion,
      };
    },
    [suggestionsByDay, infoByDay],
  );

  const habitOn = useCallback(
    (date: Date): HabitSuggestion | undefined => habitsByDay.get(dayKey(date)),
    [habitsByDay],
  );

  /** Days that carry a holiday the frontend's federal list does not have, or a live idea. */
  const extraHolidayDays = useMemo(() => {
    const days = new Map<string, Date>();
    for (const info of holidays)
      if (!info.federal) days.set(dayKey(info.observedDate), info.observedDate);
    for (const s of suggestionsByDay.values())
      days.set(dayKey(s.holiday.observedDate), s.holiday.observedDate);
    for (const s of habitsByDay.values()) days.set(dayKey(s.date), s.date);
    return Array.from(days.values());
  }, [holidays, suggestionsByDay, habitsByDay]);

  return { holidayOn, habitOn, suggestionDays: extraHolidayDays, holidays };
};
